import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../parts/modal";
import ImageChatUploadInput from "../../../../parts/inputs/image-chat-upload";
import {
  parseAthletesOrganizationInfoToClientList,
  parseCoachInformationToChat,
} from "../../../../utils/user-helpers";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import submit from "submit-form";
import SaveInputButtons from "../../../../parts/SaveInputButtons";

export const EditGroupModalComponent = ({
  athletesToShow,
  usersOrganization,
  referAsClients,
  groupParticipants,
  groupSubject,
  groupImage,
  activeUser,
  groupChatId,
  allGroupChats,
  isAutoManaged,
  groupGuests,
}) => {
  const { t } = useTranslation("coaching-portal");
  const activeUserId = activeUser._id;
  const { webAppTheme = "dark" } = activeUser;
  const participantsArray = groupParticipants.split(",");
  const guestsArray = groupGuests ? groupGuests.split(",") : [];
  const { organizationActiveUsers = [] } = usersOrganization || {};
  const referName = referAsClients
    ? t("athletes.roleClient")
    : t("athletes.roleAthlete");

  //array of coaches and athletes
  const allUsers = usersOrganization
    ? [
        ...organizationActiveUsers,
        ...parseAthletesOrganizationInfoToClientList(athletesToShow),
      ]
    : [
        ...parseCoachInformationToChat(activeUser),
        ...parseAthletesOrganizationInfoToClientList(athletesToShow),
      ];

  //State to check if we are editing the subject of the group
  const [isEditingSubject, setIsEditingSubject] = useState(false);
  //store the new participants to be added to the group chat
  const [participantState, setParticipantState] = useState([]);
  //store the participants to be removed from the group chat
  const [membersRemovedState, setMembersRemovedState] = useState([]);
  //step 1 or 2
  const [stepState, setStepState] = useState(1);
  /**
   * Filter the participants getting all the user's information
   * @param {*} acc
   * @param {*} element
   * @returns
   */
  function filterParticipants(participants, acc, element) {
    if (
      participants.includes(element._id) &&
      String(element._id) !== String(activeUserId)
    ) {
      acc.push(element);
    }
    return acc;
  }

  //actual members of the group for the first modal, to be able to remove them
  const actualMembersOfTheGroupChat = allUsers.reduce(
    (acc, user) => filterParticipants(participantsArray, acc, user),
    []
  );

  const actualGuestsOfTheGroupChat = isAutoManaged
    ? allUsers.reduce(
        (acc, user) => filterParticipants(guestsArray, acc, user),
        []
      )
    : [];

  /**
   * Send the data to the api
   */
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (stepState === 1) {
      if (membersRemovedState.length > 0) {
        submit("/actions/chat/group-chat-remove-members", {
          method: "POST",
          body: {
            participants: membersRemovedState,
            conversation: groupChatId,
          },
        });
      }
    } else {
      if (participantState.length > 0) {
        const participants = allUsers.reduce(
          (acc, user) => filterParticipants(participantState, acc, user),
          []
        );

        submit("/actions/chat/group-chat-add-members", {
          method: "POST",
          body: {
            participants,
            conversation: groupChatId,
          },
        });
      }
    }
  };

  //array of users excluding the actual participants to show them in the second modal
  const arrayWithoutActualParticipants = allUsers.filter(
    (user) => !participantsArray.includes(user._id)
  );

  return (
    <Modal
      className={"create-chat-group-modal"}
      toClear={["isAutoManaged"]}
      overflowModal={true}
      chatGroupEdit={true}
    >
      <div className="head border-divider main-title-edit-group-chat">
        <div>
          {stepState === 2 && (
            <img
              width={30}
              height={30}
              src="/img/svg/icon_arrow-left.svg"
              alt={t("chat.editGroupModal.backAlt")}
              className={
                webAppTheme === "dark"
                  ? "filter-white cursor-pointer"
                  : "filter-black cursor-pointer"
              }
              onClick={() => {
                setStepState(1);
              }}
            />
          )}
          <h2 className="alt">{t("chat.editGroupModal.title")}</h2>
        </div>
        <a
          href={`/actions/refresh?groupParticipants=&groupSubject=&allGroupChats=&groupChatId=&groupImage=&groupEdited=&modal=`}
        >
          <img
            className="icon-red"
            src="/img/new-icons/red-cross.svg"
            alt={t("chat.editGroupModal.dismissAlt")}
          />
        </a>
      </div>

      {stepState === 1 ? (
        <Step1NewGroup
          allUsers={allUsers}
          referAsClients={referAsClients}
          referName={referName}
          groupSubject={groupSubject}
          groupImage={groupImage}
          participantsArray={participantsArray}
          actualMembersOfTheGroupChat={actualMembersOfTheGroupChat}
          membersRemovedState={membersRemovedState}
          setMembersRemovedState={setMembersRemovedState}
          groupChatId={groupChatId}
          allGroupChats={allGroupChats}
          isEditingSubject={isEditingSubject}
          setIsEditingSubject={setIsEditingSubject}
          webAppTheme={webAppTheme}
          isAutoManaged={isAutoManaged}
          actualGuestsOfTheGroupChat={actualGuestsOfTheGroupChat}
        />
      ) : (
        <Step2NewGroup
          participantState={participantState}
          referName={referName}
          arrayWithoutActualParticipants={arrayWithoutActualParticipants}
          setParticipantState={setParticipantState}
          webAppTheme={webAppTheme}
        />
      )}
      {!isAutoManaged && (
        <div className="modal-footer foot modal-footer-group-chat">
          <div
            className={
              stepState === 2
                ? "footer-group-chat-modal"
                : "footer-group-chat-modal footer-group-chat-modal-add"
            }
          >
            {stepState === 2 ? (
              <div>
                <button
                  type="button"
                  className="clear-all-group-chat"
                  onClick={() => {
                    setParticipantState([]);
                  }}
                >
                  {t("chat.editGroupModal.clearAll")}
                </button>
              </div>
            ) : (
              <div>
                <button
                  type="button"
                  disabled={isEditingSubject}
                  className="go-add-members-chat-group"
                  onClick={() => {
                    setStepState(2);
                  }}
                >
                  {t("chat.editGroupModal.addMembers")}
                </button>
              </div>
            )}
            <div>
              <button
                disabled={isEditingSubject}
                id="next-group-chat"
                className="next-group-chat"
                onClick={handleFormSubmit}
                type="button"
                data-button-type="primary"
              >
                <span>{t("chat.editGroupModal.save")}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

const Step1NewGroup = ({
  allUsers,
  referAsClients,
  referName,
  groupSubject,
  groupImage,
  participantsArray,
  actualMembersOfTheGroupChat,
  membersRemovedState,
  setMembersRemovedState,
  groupChatId,
  allGroupChats,
  isEditingSubject,
  setIsEditingSubject,
  webAppTheme,
  isAutoManaged,
  actualGuestsOfTheGroupChat,
}) => {
  const { t } = useTranslation("coaching-portal");
  const rolesLower = referAsClients
    ? t("athletes.roleClientsLower")
    : t("athletes.roleAthletesLower");
  /**
   * Function to add the participants to be removed
   * @param {*} value
   */
  const handleSelectionChange = (value) => {
    let data = JSON.parse(JSON.stringify(membersRemovedState));
    if (!membersRemovedState.includes(value)) {
      data.length === 1 && data[0].length === 0
        ? (data = [value])
        : data.push(value);
    } else {
      if (membersRemovedState.length > 0) {
        const filtered = data.filter((val) => val != value);
        data = filtered.length > 0 ? filtered : [];
      }
    }
    setMembersRemovedState(data);
  };

  const formRef = useRef(null);

  //state to the form
  const [groupInformationState, setGroupInformationState] = useState({
    groupImage,
    groupSubject,
  });

  /**
   * Set the subject's value
   * @param {*} param0
   */
  const handleInputSubjectChange = ({ target }) => {
    const newState = Object.assign({}, groupInformationState, {
      groupSubject: target.value,
    });
    setGroupInformationState(newState);
  };

  /**
   * Function to send the image when is changed
   */
  const handleImageUpload = () => {
    if (formRef.current) {
      const submitButton = formRef.current.querySelector(
        'button[type="submit"]'
      );
      if (submitButton) {
        submitButton.click();
      }
    }
  };

  /**
   * Reset the subject's value
   * @param {*} param0
   */
  const handleResetInputSubject = (e) => {
    e.preventDefault();
    const newState = Object.assign({}, groupInformationState, {
      groupSubject,
    });
    setIsEditingSubject(false);
    setGroupInformationState(newState);
  };

  /**
   * submit the new subject's value
   * @param {*} param0
   */
  const handleSaveSubject = (e, allGroupChats, conversationId) => {
    e.preventDefault();
    submit("/actions/chat/group-chat-edit", {
      method: "POST",
      body: {
        subject: groupInformationState.groupSubject,
        allGroupChats,
        conversationId,
      },
    });
    setIsEditingSubject(false);
  };

  /**
   * Validate if subject was changed
   * @returns
   */
  const formChanged = () => {
    return groupInformationState.groupSubject !== groupSubject;
  };

  return (
    <div id="step-edit-group-chat">
      <div className="step1-group-chat">
        <div>
          <span className="add-participants-title-group-chat">
            {t("chat.editGroupModal.groupSubjectAndPicture")}
          </span>
          <div className="image-name-group-chat">
            {/* this form is hidden, this is just to send the image to the backend (we also have to send the chat id and all the group chats, for that reason are the hidden inputs)*/}
            <form
              encType="multipart/form-data"
              method="POST"
              data-noreset
              action={`/actions/chat/group-chat-edit`}
              ref={formRef}
            >
              <input
                name="conversationId"
                defaultValue={groupChatId}
                type="hidden"
              />
              <input
                name="allGroupChats"
                defaultValue={allGroupChats}
                type="hidden"
              />
              <input
                name="subject"
                defaultValue={groupInformationState.groupSubject}
                type="hidden"
              />
              <ImageChatUploadInput
                name="image"
                prefix={t("chat.editGroupModal.groupChatPrefix")}
                defaultValue={groupInformationState.groupImage}
                hasImage={true}
                isDisable={isEditingSubject}
                handleImageUpload={handleImageUpload}
              />
              <button type="submit" style={{ display: "none" }} />
            </form>
            {/* end of hidden form for the image */}
            <div className="subject-group-chat-edit">
              <input
                name="subject"
                className="search-user-input-edit-group-chat"
                value={groupInformationState.groupSubject}
                disabled={false}
                onChange={handleInputSubjectChange}
                onFocus={() => setIsEditingSubject(true)}
              />
              <SaveInputButtons
                enabled={isEditingSubject}
                handleSave={(e) => {
                  handleSaveSubject(e, allGroupChats, groupChatId);
                }}
                handleDismiss={(e) => handleResetInputSubject(e)}
                saveDisabled={!formChanged()}
              />
            </div>
          </div>
        </div>
      </div>

      {!isAutoManaged && (
        <div className="gym-modal-main-table">
          <div className="add-participants-main-group-chat">
            <span className="add-participants-title-group-chat">
              {t("chat.editGroupModal.participantsCount", {
                selected: participantsArray.length,
                total: allUsers.length,
              })}
            </span>
          </div>
          <div className="list-group-chat-participants-main">
            {actualMembersOfTheGroupChat.map((item) => {
              const { name = "", coachId = "", _id = "" } = item;

              return (
                <div key={_id} className="card-group-chat-participant">
                  <div>
                    <div
                      className={
                        item.avatar && item.avatar.s3Path
                          ? "avatar-group-chat"
                          : "avatar-group-chat avatar-group-chat-border"
                      }
                      style={{
                        background: `url(${getAvatarUrl(
                          item,
                          false,
                          webAppTheme
                        )})`,
                        backgroundPosition: "center center",
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                      }}
                    ></div>
                    <div className="identifier-group-chat">
                      <span className="name-group-chat">{name}</span>
                      <span className="role-group-chat">
                        {coachId ? referName : t("chat.editGroupModal.coach")}
                      </span>
                    </div>
                  </div>
                  <div className="remove-add-group-chat">
                    {!membersRemovedState.includes(_id) ? (
                      <button
                        type="button"
                        disabled={isEditingSubject}
                        className="remove-member-chat-group"
                        onClick={() => handleSelectionChange(_id)}
                      >
                        {t("chat.editGroupModal.remove")}
                      </button>
                    ) : (
                      <button
                        disabled={isEditingSubject}
                        className="add-remove-member-chat-group-button"
                        onClick={() => handleSelectionChange(_id)}
                        type="button"
                        data-button-type="primary"
                      >
                        <span>{t("chat.editGroupModal.add")}</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isAutoManaged && (
        <>
          <div className="auto-managed-info-box">
            <p>
              <strong>{t("chat.editGroupModal.autoManagedNoticeBold")}</strong>{" "}
              {t("chat.editGroupModal.autoManagedNoticeBody", { rolesLower })}
            </p>
          </div>
          <div className="gym-modal-main-table">
            <div className="add-participants-main-group-chat">
              <span className="add-participants-title-group-chat">
                {t("chat.editGroupModal.membersCount", {
                  count: actualGuestsOfTheGroupChat.length,
                })}
              </span>
            </div>
            <div className="list-group-chat-participants-main">
              {actualGuestsOfTheGroupChat.map((item) => {
                const { name = "", coachId = "", _id = "" } = item;

                return (
                  <div key={_id} className="card-group-chat-participant">
                    <div>
                      <div
                        className={
                          item.avatar && item.avatar.s3Path
                            ? "avatar-group-chat"
                            : "avatar-group-chat avatar-group-chat-border"
                        }
                        style={{
                          background: `url(${getAvatarUrl(
                            item,
                            false,
                            webAppTheme
                          )})`,
                          backgroundPosition: "center center",
                          backgroundSize: "cover",
                          backgroundRepeat: "no-repeat",
                        }}
                      ></div>
                      <div className="identifier-group-chat">
                        <span className="name-group-chat">{name}</span>
                        <span className="role-group-chat">
                          {coachId ? referName : t("chat.editGroupModal.coach")}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Step2NewGroup = ({
  participantState,
  referName,
  arrayWithoutActualParticipants,
  setParticipantState,
  webAppTheme,
}) => {
  const { t } = useTranslation("coaching-portal");
  //store the filter value
  const [filterValue, setFilterValue] = useState("");

  //array of filtered users
  const filteredUsers = filterValue
    ? arrayWithoutActualParticipants.filter((user) =>
        user.name.toLowerCase().startsWith(filterValue.toLowerCase())
      )
    : arrayWithoutActualParticipants;

  /**
   * Select the participants
   * @param {*} value
   */
  const handleSelectionChange = (value) => {
    let data = JSON.parse(JSON.stringify(participantState));
    if (!participantState.includes(value)) {
      //maximum 100 participants
      if (participantState.length < 100) {
        data.length === 1 && data[0].length === 0
          ? (data = [value])
          : data.push(value);
      }
    } else {
      if (participantState.length > 0) {
        const filtered = data.filter((val) => val != value);
        data = filtered.length > 0 ? filtered : [];
      }
    }
    setParticipantState(data);
  };

  return (
    <div id="step-1-group-chat">
      <div className="step1-group-chat">
        <div className="search-user-group-chat">
          <input
            type="search"
            id="filter-group-chat"
            className="search-user-input-group-chat"
            placeholder={t("chat.editGroupModal.searchUserPlaceholder")}
            value={filterValue}
            onChange={({ target: { value } }) => setFilterValue(value)}
          />
        </div>
      </div>

      {filteredUsers.length === 0 && filterValue.length > 0 ? (
        <div className="empty-filter-chat-state">
          <p className="s1">{t("chat.editGroupModal.noUsersFound")}</p>
        </div>
      ) : (
        <div className="gym-modal-main-table">
          <div className="add-participants-main-group-chat">
            <span className="add-participants-title-group-chat">
              {t("chat.editGroupModal.addParticipants")}
            </span>
            <span className="add-participants-length-group-chat clipped">
              (
              {participantState.length && participantState[0].length === 0
                ? 0
                : participantState.length}
              )
            </span>
          </div>
          <div className="list-group-chat-participants-main">
            {filteredUsers.map((item) => {
              const { name = "", coachId = "", _id = "" } = item;

              return (
                <div key={_id} className="card-group-chat-participant">
                  <div>
                    <div
                      className={
                        item.avatar && item.avatar.s3Path
                          ? "avatar-group-chat"
                          : "avatar-group-chat avatar-group-chat-border"
                      }
                      style={{
                        background: `url(${getAvatarUrl(
                          item,
                          false,
                          webAppTheme
                        )})`,
                        backgroundPosition: "center center",
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                      }}
                    ></div>
                    <div className="identifier-group-chat">
                      <span className="name-group-chat">{name}</span>
                      <span className="role-group-chat">
                        {coachId ? referName : t("chat.editGroupModal.coach")}
                      </span>
                    </div>
                  </div>
                  <div
                    className={
                      participantState.includes(_id)
                        ? "circle-selector-container-selected"
                        : "circle-selector-container"
                    }
                  >
                    <div
                      className={
                        participantState.includes(_id)
                          ? "radio radio-group-selected-group-chat"
                          : "radio-video-list"
                      }
                      onClick={() => handleSelectionChange(_id)}
                    >
                      {participantState.includes(_id) && (
                        <img
                          src="/img/new-icons/vuesax-linear-tick-circle-gradient.svg"
                          alt={t("chat.editGroupModal.checkedAlt")}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
